import { FC } from 'react';

import { changeSplashScreen, setUser } from 'store/auth/authSlice';
import { SAMLAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import PrimaryButton from 'components/Common/PrimaryButton';
import { auth } from 'utils/config/firebase';
import { useAppDispatch } from 'app/hooks';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { Logo } from '../../assets/image-assets/images';
import {
    getDefaultFiltersQuery,
    getUserDefaultFiltersQuery,
    getUserSettingsQuery,
} from 'api/queries/user';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useTranslation } from 'react-i18next';
import { errorLogger } from 'utils/config/firebase/logs';
import { toast } from 'react-toastify';
import { fadeAnimation } from 'helpers/messages';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuthRedirect } from 'hooks/useAuthRedirect';

const SignIn: FC = () => {
    const provider = new SAMLAuthProvider('saml.randstad');
    const { t } = useTranslation();
    const { redirect } = useAuthRedirect();
    const dispatch = useAppDispatch();

    const onSubmit = async () => {
        try {
            await signInWithPopup(auth, provider);
            dispatch(changeSplashScreen(true));
            const user = auth.currentUser;
            if (!user) {
                throw new Error();
            }
            dispatch(setUser({ isLoggedIn: true, ...user }));
            getUserSettingsQuery(dispatch);
            getUserDefaultFiltersQuery(dispatch);
            getDefaultFiltersQuery(dispatch);
            redirect();
        } catch (error) {
            if (error instanceof Error) {
                let message = error?.message;
                if (error?.message.includes('PERMISSION_DENIED')) {
                    message = t('web_app.auth.block_error');
                }
                errorLogger.critical(message, 'login');
                toast.error(message, {
                    position: 'top-center',
                    hideProgressBar: true,
                    icon: <ErrorOutlineIcon />,
                    transition: fadeAnimation,
                });
            }
            signOut(auth);
            console.log(error);
            dispatch(changeSplashScreen(false));
        }
    };

    return (
        <Box className="sign-in">
            <Box className="banner-image" />
            <Box className="sign-in-content">
                <Box>
                    <LazyLoadImage alt={t('web_app.sign_in.header')} src={Logo} />
                    <Typography variant="h1">{t('web_app.sign_in.header')}</Typography>
                </Box>
                <Box className="sign-in-content-btn">
                    <Typography variant="h5">{t('web_app.sign_in.title')}</Typography>
                    <PrimaryButton
                        text={t('web_app.sign_in.button_name')}
                        onClick={onSubmit}
                        className="filled"
                    />
                    <Box className="single-sign-on">
                        <Typography variant="body1">{t('web_app.sign_in.description')}</Typography>
                    </Box>
                </Box>
                <Box className="version">{t('web_app.sign_in.version')}</Box>
            </Box>
        </Box>
    );
};

export default SignIn;
